import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { getUser, getToken, clearSession } from "@/lib/api";
import api from "@/lib/api";
import { getClientId } from "./clientId";
import { Toaster } from "sonner";
import { LayoutGrid, Terminal, Landmark, BarChart3, Truck, Package, LogOut, Store } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_4a2dee35-189a-4f59-95bd-a089560b8efb/artifacts/rzc72v1x_thara%20%281%29.png";

const LINKS = [
  { to: "/pos", label: "POS", icon: LayoutGrid, end: true },
  { to: "/pos/register", label: "Cash Register", icon: Landmark },
  { to: "/pos/terminals", label: "Terminals", icon: Terminal },
  { to: "/pos/requests", label: "Stock Requests", icon: Truck },
  { to: "/pos/stock", label: "Current Stock", icon: Package },
  { to: "/pos/report", label: "Sales Report", icon: BarChart3 },
];

export default function POSShell() {
  const navigate = useNavigate();
  const loc = useLocation();
  const [reg, setReg] = useState(null);
  const [terminal, setTerminal] = useState(null);
  const u = getUser();

  useEffect(() => {
    if (!getToken() || !u || !["branch", "franchise"].includes(u.department)) {
      navigate("/pos/login", { replace: true });
    }
  }, [navigate, u]);

  // Load the current terminal + register from localStorage
  useEffect(() => {
    const tid = localStorage.getItem("pos_terminal_id");
    if (tid) {
      api.get("/pos/terminals").then((r) => {
        const t = (r.data || []).find((x) => x.id === tid);
        setTerminal(t);
        if (t) api.get("/pos/cash-registers/current", { params: { terminal_id: t.id, client_id: getClientId() } }).then((rr) => setReg(rr.data));
      });
    }
  }, [loc.pathname]);

  const logout = () => {
    clearSession();
    navigate("/pos/login", { replace: true });
  };

  if (!u) return null;

  return (
    <div className="min-h-screen bg-slate-100 flex">
      <Toaster richColors position="top-right" />
      {/* Sidebar */}
      <aside className="w-56 bg-slate-900 text-white flex flex-col shrink-0">
        <div className="h-16 flex items-center gap-3 px-4 border-b border-white/10">
          <div className="bg-white rounded p-1"><img src={LOGO_URL} alt="" className="h-8 w-auto" /></div>
          <div className="leading-tight">
            <p className="text-sm font-bold">Thara POS</p>
            <p className="text-[10px] uppercase tracking-widest text-white/60">Terminal Ops</p>
          </div>
        </div>
        <nav className="flex-1 py-3 space-y-1">
          {LINKS.map((l) => {
            const Icon = l.icon;
            return (
              <NavLink key={l.to} to={l.to} end={l.end}
                className={({ isActive }) => `flex items-center gap-3 mx-2 rounded-md px-3 py-2 text-sm font-medium transition-colors ${isActive ? "bg-red-600 text-white" : "text-white/80 hover:bg-white/10"}`}
                data-testid={`posnav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}>
                <Icon className="h-4 w-4" /> {l.label}
              </NavLink>
            );
          })}
        </nav>
        <div className="border-t border-white/10 p-3 space-y-2">
          <div className="text-[11px] text-white/60">
            <div className="flex items-center gap-1"><Store className="h-3 w-3" />{u.location_name || "Store"}</div>
            <div className="mt-0.5">{u.name} · {u.role}</div>
          </div>
          <button onClick={logout} className="w-full h-8 rounded bg-white/10 hover:bg-white/20 text-xs flex items-center justify-center gap-1"><LogOut className="h-3 w-3" /> Logout</button>
        </div>
      </aside>

      {/* Top bar + content */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-14 bg-white border-b border-slate-200 px-4 flex items-center gap-3">
          <span className="text-xs text-slate-500 uppercase tracking-widest font-semibold">Terminal</span>
          <TerminalPicker terminal={terminal} onChange={setTerminal} />
          <span className="text-xs text-slate-400">·</span>
          <RegisterPill reg={reg} onOpened={setReg} terminal={terminal} />
          <span className="ml-auto text-xs text-slate-500 tabular-nums">{new Date().toLocaleString()}</span>
        </header>
        <main className="flex-1 overflow-auto">
          <Outlet context={{ terminal, register: reg, refreshRegister: () => {
            if (terminal) api.get("/pos/cash-registers/current", { params: { terminal_id: terminal.id, client_id: getClientId() } }).then((rr) => setReg(rr.data));
          } }} />
        </main>
      </div>
    </div>
  );
}

function TerminalPicker({ terminal, onChange }) {
  const [list, setList] = useState([]);
  useEffect(() => {
    api.get("/pos/terminals").then((r) => setList(r.data || []));
  }, []);
  return (
    <select
      value={terminal?.id || ""}
      onChange={(e) => {
        const t = list.find((x) => x.id === e.target.value);
        onChange(t);
        if (t) localStorage.setItem("pos_terminal_id", t.id);
      }}
      className="h-8 rounded-md border border-slate-200 text-xs px-2 bg-white"
      data-testid="pos-terminal-picker"
    >
      <option value="">— Pick terminal —</option>
      {list.map((t) => <option key={t.id} value={t.id}>{t.code} · {t.name}</option>)}
    </select>
  );
}

function RegisterPill({ reg, terminal, onOpened }) {
  if (!terminal) return <span className="text-xs text-slate-400">Select a terminal</span>;
  if (!reg) return (
    <a href="/pos/register" className="text-xs font-semibold text-red-700 hover:underline" data-testid="pos-open-cr-link">
      Cash register CLOSED — open now →
    </a>
  );
  if (reg.locked_by_other) return (
    <span className="text-xs font-semibold text-amber-700 flex items-center gap-1" data-testid="pos-cr-locked">
      <span className="h-1.5 w-1.5 rounded-full bg-amber-500" /> Terminal locked to another workstation ({reg.opened_by_name})
    </span>
  );
  return (
    <span className="text-xs font-semibold text-emerald-700 flex items-center gap-1" data-testid="pos-cr-status">
      <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" /> Register {reg.reg_no} · open since {new Date(reg.opened_at).toLocaleTimeString()}
    </span>
  );
}
